import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventDetails } from '@/hooks/useEventDetails/useEventDetails';
import {
  useConfirmEventMutation,
  useDeclineEventMutation,
  usePatchEventMutation,
  useCancelEventMutation,
  useMarkNoOccurrenceMutation,
  useResendSurveyMutation,
} from '@/store/api/eventService/eventServiceApi';
import { spaceConfigServiceApi } from '@/store/api/spaceConfigService/spaceConfigServiceApi';
import type { EventData, EventSpaceSelection } from '@/store/models/events/entities';
import { type EventId } from '@/store/models/ids';
import type { Building } from '@/store/models/buildings';
import type { SpaceWithAvailability } from '@/store/models/spaces';
import type { PatchEventRequest } from '@/store/models';
import type { EventReviewEditInput } from '@/types/planner.types';
import {
  convertBuildingLocalDateTimeToUtcIso,
  formatUtcTimeInTimeZone,
  getIsoDateInTimeZone,
} from '@/utils/timeZone';

function mapActionError(error: unknown): string {
  const status =
    typeof error === 'object' && error !== null && 'status' in error
      ? (error as { status?: number | string }).status
      : undefined;

  if (status === 409 || status === 412 || status === 428) {
    return 'conflict';
  }

  if (status === 400 || status === 403 || status === 422) {
    return 'invalidState';
  }

  return 'Unable to complete your request right now. Please try again.';
}

function extractApiErrorMessage(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const errorWithData = error as { data?: unknown; error?: string };
  const data = errorWithData.data;

  if (typeof data === 'string' && data.trim().length > 0) {
    return data;
  }

  if (data && typeof data === 'object') {
    const obj = data as {
      message?: unknown;
      error?: unknown;
      title?: unknown;
      detail?: unknown;
      errors?: unknown;
    };

    if (obj.error && typeof obj.error === 'object') {
      const nestedError = obj.error as {
        message?: unknown;
        details?: unknown;
      };

      // Prefer details over message — message is often a generic wrapper like
      // "One or more validation errors occurred." while details carries
      // actionable field-level information.
      if (typeof nestedError.details === 'string' && nestedError.details.trim().length > 0) {
        return nestedError.details;
      }

      if (nestedError.details && typeof nestedError.details === 'object') {
        const firstDetail = Object.values(nestedError.details as Record<string, string[]>)
          .flat()
          .find((v): v is string => typeof v === 'string' && v.trim().length > 0);
        if (firstDetail) {
          return firstDetail;
        }
      }

      if (typeof nestedError.message === 'string' && nestedError.message.trim().length > 0) {
        return nestedError.message;
      }
    }

    for (const candidate of [obj.message, obj.error, obj.title, obj.detail]) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate;
      }
    }

    const { errors } = obj;
    if (Array.isArray(errors) && errors.length > 0) {
      const first = errors.find(
        (item: unknown): item is string => typeof item === 'string' && item.trim().length > 0
      );
      if (first) {
        return first;
      }
    }
  }

  if (typeof errorWithData.error === 'string' && errorWithData.error.trim().length > 0) {
    return errorWithData.error;
  }

  return undefined;
}

function getLocalTimeInputValue(originalIso: string, timeZone?: string): string {
  if (!originalIso) {
    return '';
  }

  if (!timeZone) {
    const match = originalIso.match(/T(\d{2}:\d{2})/);
    return match?.[1] ?? '';
  }

  return formatUtcTimeInTimeZone(originalIso, 'en-GB', timeZone, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function getLocalDateInputValue(isoDateTime: string, timeZone?: string): string {
  if (!isoDateTime) {
    return '';
  }

  if (!timeZone) {
    return isoDateTime.split('T')[0] ?? '';
  }

  return getIsoDateInTimeZone(isoDateTime, timeZone);
}

function mergeDateTimeWithBuildingTimeZone(
  datePart: string,
  timePart: string | undefined,
  originalIso: string,
  timeZone?: string
): string {
  if (!datePart) {
    return originalIso;
  }

  // Compare wall-clock inputs before conversion: metadata-only edits must retain
  // seconds, offsets and DST ambiguity in the original timestamps.
  if (
    datePart === getLocalDateInputValue(originalIso, timeZone) &&
    (!timePart || timePart === getLocalTimeInputValue(originalIso, timeZone))
  ) {
    return originalIso;
  }

  const effectiveTime =
    timePart && timePart.trim().length > 0
      ? timePart
      : getLocalTimeInputValue(originalIso, timeZone);

  return convertBuildingLocalDateTimeToUtcIso(datePart, effectiveTime, timeZone);
}

function normalizeCostCenter(value: string): string {
  return value.replace(/^CC\s*-\s*/i, '').trim();
}

function isSameContact(
  left: { name?: string; email?: string } | undefined,
  right: { name?: string; email?: string } | undefined
): boolean {
  return (left?.name ?? '') === (right?.name ?? '') && (left?.email ?? '') === (right?.email ?? '');
}

function getPreferredSetupCodes(event: EventData | undefined): string[] {
  const setupCodes = event?.spaces?.[0]?.selectedSetupCodes;
  if (setupCodes && setupCodes.length > 0) {
    return setupCodes;
  }

  return [];
}

function areSameSetupCodes(left: string[], right: string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }

  const leftSorted = [...left].sort();
  const rightSorted = [...right].sort();
  return leftSorted.every((value, index) => value === rightSorted[index]);
}

export interface UseReviewActionsResult {
  selectedEventId: string | null;
  panelOpen: boolean;
  selectedSpaceId: string | null;
  selectedSpaces: EventSpaceSelection[];
  isConfirming: boolean;
  isDeclining: boolean;
  isCancelling: boolean;
  isMarkingNoOccurrence: boolean;
  isSavingEdit: boolean;
  declineDialogOpen: boolean;
  cancelDialogOpen: boolean;
  error: string | null;
  event: EventData | undefined;
  building: Building | undefined;
  spaces: SpaceWithAvailability[];
  buildings: Building[];
  selectedSpaceStateProvince: string;
  isLoading: boolean;
  isError: boolean;
  openPanel: (eventId: string) => void;
  closePanel: () => void;
  setSelectedSpace: (spaceId: string) => Promise<boolean>;
  setSelectedSpaces: (spaces: EventSpaceSelection[]) => void;
  clearSelectedSpace: () => void;
  confirmEvent: () => Promise<boolean>;
  declineEvent: (reason: string) => Promise<boolean>;
  cancelEvent: (reason: string) => Promise<boolean>;
  confirmCancellation: () => Promise<boolean>;
  markNoOccurrence: () => Promise<boolean>;
  saveEdit: (input: EventReviewEditInput) => Promise<boolean>;
  openDeclineDialog: () => void;
  closeDeclineDialog: () => void;
  openCancelDialog: () => void;
  closeCancelDialog: () => void;
  refetch: () => void;
  clearError: () => void;
  isSearchingSpaces: boolean;
  isResendingSurvey: boolean;
  resendSurvey: () => Promise<boolean>;
  fetchSpacesAvailability: (dateTimeOverrides?: {
    startDate?: string;
    endDate?: string;
    startTime?: string;
    endTime?: string;
    buildingIds?: string[];
  }) => void;
}

export function useReviewActions(): UseReviewActionsResult {
  const { t } = useTranslation('planner');
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const [selectedSpaces, setSelectedSpacesState] = useState<EventSpaceSelection[]>([]);

  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { event, building, etag, isLoading, isError, refetch } = useEventDetails(
    selectedEventId as EventId | null,
    { skipSpaces: true }
  );

  // Initialize selectedSpaces from event data when it loads
  const spacesInitializedForEventRef = useRef<string | null>(null);
  useEffect(() => {
    if (!event) return;
    const eventId = event.eventId as string;
    const alreadyInitialized = spacesInitializedForEventRef.current === eventId;

    if (event.spaces && event.spaces.length > 0) {
      if (alreadyInitialized) return;
      spacesInitializedForEventRef.current = eventId;
      setSelectedSpacesState(event.spaces);
    } else if (event.spaceId) {
      if (alreadyInitialized) return;
      spacesInitializedForEventRef.current = eventId;
      const spaceId = event.spaceId;
      const spaceName = event.spaceName ?? '';
      const setupCodes = getPreferredSetupCodes(event);
      setSelectedSpacesState([
        {
          spaceId,
          spaceName,
          selectedSetupCodes: setupCodes,
        },
      ]);
    }
  }, [event]);

  const { data: plannerBuildingsData } = spaceConfigServiceApi.useGetPlannerBuildingsQuery();

  // Spaces with real-time availability, fetched via mutation when the panel opens.
  const [spaces, setSpaces] = useState<SpaceWithAvailability[]>([]);
  const [isSearchingSpaces, setIsSearchingSpaces] = useState(false);

  // Real-time availability: use a mutation (not a lazy query) so RTK Query
  // does not auto-cancel the in-flight request on dependency changes.
  const [searchSpacesAvailability] = spaceConfigServiceApi.useSearchSpacesAvailabilityMutation();

  const fetchSpacesAvailability = useCallback(
    (dateTimeOverrides?: {
      startDate?: string;
      endDate?: string;
      startTime?: string;
      endTime?: string;
      buildingIds?: string[];
    }) => {
      if (!event) return;

      // Only include date/time when the caller explicitly provides them
      // (edit mode, date/time changes). View-mode calls pass only buildingIds.
      const hasDateTimeOverrides = !!(
        dateTimeOverrides?.startDate ||
        dateTimeOverrides?.startTime ||
        dateTimeOverrides?.endDate ||
        dateTimeOverrides?.endTime
      );

      const eventTimeZone = building?.gmtZone ?? event?.building?.gmtZone;

      const startDate = hasDateTimeOverrides
        ? (dateTimeOverrides?.startDate ??
          (eventTimeZone ? getLocalDateInputValue(event.eventStartDate, eventTimeZone) : undefined))
        : undefined;
      const endDate = hasDateTimeOverrides
        ? (dateTimeOverrides?.endDate ??
          (eventTimeZone ? getLocalDateInputValue(event.eventEndDate, eventTimeZone) : undefined))
        : undefined;
      const startTime = hasDateTimeOverrides
        ? (dateTimeOverrides?.startTime ??
          (eventTimeZone ? getLocalTimeInputValue(event.eventStartDate, eventTimeZone) : undefined))
        : undefined;
      const endTime = hasDateTimeOverrides
        ? (dateTimeOverrides?.endTime ??
          (eventTimeZone ? getLocalTimeInputValue(event.eventEndDate, eventTimeZone) : undefined))
        : undefined;

      // Clear previous results when re-fetching
      setSpaces([]);
      setIsSearchingSpaces(true);

      // Search only the specified buildings (required)
      const buildingIds = dateTimeOverrides?.buildingIds;
      if (!buildingIds || buildingIds.length === 0) {
        setIsSearchingSpaces(false);
        return;
      }

      searchSpacesAvailability({
        buildingIds,
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate: endDate || startDate } : {}),
        ...(startTime ? { startTime } : {}),
        ...(endTime ? { endTime } : {}),
      })
        .unwrap()
        .then((result) => {
          if (result.success && Array.isArray(result.data)) {
            setSpaces(result.data);
          }
        })
        .catch(() => {
          // Error fetching spaces
        })
        .finally(() => {
          setIsSearchingSpaces(false);
        });
    },
    [event, building, searchSpacesAvailability]
  );

  const buildings = useMemo(
    () => plannerBuildingsData?.buildings ?? [],
    [plannerBuildingsData?.buildings]
  );
  const selectedOrCurrentSpaceId = selectedSpaceId ?? event?.spaces?.[0]?.spaceId ?? event?.spaceId;
  const selectedOrCurrentSpace = useMemo(
    () => spaces.find((item) => item.spaceId === selectedOrCurrentSpaceId),
    [spaces, selectedOrCurrentSpaceId]
  );
  const effectiveBuilding = useMemo(() => {
    const selectedBuildingId = selectedOrCurrentSpace?.buildingId;
    const selectedBuilding = selectedBuildingId
      ? buildings.find((item) => item.buildingId === selectedBuildingId)
      : undefined;

    return selectedBuilding ?? building;
  }, [selectedOrCurrentSpace?.buildingId, buildings, building]);
  const selectedSpaceStateProvince = useMemo(() => {
    const selectedBuildingId = selectedOrCurrentSpace?.buildingId;
    const selectedBuilding = selectedBuildingId
      ? buildings.find((item) => item.buildingId === selectedBuildingId)
      : undefined;

    return (
      selectedBuilding?.stateName ??
      selectedBuilding?.stateCode ??
      building?.stateName ??
      building?.stateCode ??
      event?.building?.gmtZone ??
      ''
    );
  }, [selectedOrCurrentSpace?.buildingId, buildings, building, event?.building?.gmtZone]);

  const [confirmEventMutation, { isLoading: isConfirming }] = useConfirmEventMutation();
  const [declineEventMutation, { isLoading: isDeclining }] = useDeclineEventMutation();
  const [cancelEventMutation, { isLoading: isCancelling }] = useCancelEventMutation();
  const [markNoOccurrenceMutation, { isLoading: isMarkingNoOccurrence }] =
    useMarkNoOccurrenceMutation();
  const [patchEventMutation, { isLoading: isSavingEdit }] = usePatchEventMutation();
  const [resendSurveyMutation, { isLoading: isResendingSurvey }] = useResendSurveyMutation();

  const openPanel = useCallback((eventId: string) => {
    setSelectedEventId(eventId);
    setPanelOpen(true);
    setSelectedSpaceId(null);
    setSelectedSpacesState([]);
    spacesInitializedForEventRef.current = null;
    setError(null);
  }, []);

  const closePanel = useCallback(() => {
    setPanelOpen(false);
    setSelectedEventId(null);
    setSelectedSpaceId(null);
    setSelectedSpacesState([]);
    spacesInitializedForEventRef.current = null;
    setError(null);
    setDeclineDialogOpen(false);
    setCancelDialogOpen(false);
    setSpaces([]);
  }, []);

  const setSelectedSpace = useCallback((spaceId: string) => {
    setSelectedSpaceId(spaceId);
    setError(null);

    // Keep selected space as a staged change and persist it only in saveEdit.
    return Promise.resolve(true);
  }, []);

  const clearSelectedSpace = useCallback(() => {
    setSelectedSpaceId(null);
  }, []);

  const setSelectedSpaces = useCallback((newSpaces: EventSpaceSelection[]) => {
    setSelectedSpacesState(newSpaces);
    // Keep selectedSpaceId in sync with the first (primary) space
    const primary = newSpaces.find((s) => s.spaceId);
    setSelectedSpaceId(primary ? (primary.spaceId as string) : null);
  }, []);

  const confirmEvent = useCallback(async () => {
    if (!selectedEventId || !event?.buildingId) return false;
    setError(null);

    try {
      await confirmEventMutation({
        eventId: selectedEventId as EventId,
        buildingId: event.buildingId,
        etag,
      }).unwrap();
      closePanel();
      return true;
    } catch (apiError) {
      setError(extractApiErrorMessage(apiError) ?? mapActionError(apiError));
      return false;
    }
  }, [selectedEventId, event?.buildingId, confirmEventMutation, closePanel, etag]);

  const declineEvent = useCallback(
    async (reason: string) => {
      if (!selectedEventId || !event?.buildingId || !reason.trim()) return false;
      setError(null);

      const eventId = selectedEventId as EventId;
      const buildingId = event.buildingId;

      // Unsubscribe from event query before the mutation so the tag
      // invalidation doesn't trigger a doomed refetch that gets aborted.
      setSelectedEventId(null);

      try {
        await declineEventMutation({
          eventId,
          buildingId,
          comments: reason,
          etag,
        }).unwrap();
        setDeclineDialogOpen(false);
        closePanel();
        return true;
      } catch (apiError) {
        // Restore subscription so the panel can show the error
        setSelectedEventId(eventId as string);
        setError(extractApiErrorMessage(apiError) ?? mapActionError(apiError));
        return false;
      }
    },
    [selectedEventId, event?.buildingId, declineEventMutation, closePanel, etag]
  );

  const cancelEvent = useCallback(
    async (reason: string) => {
      if (!selectedEventId || !event?.buildingId || !reason.trim()) return false;
      setError(null);

      const eventId = selectedEventId as EventId;
      const buildingId = event.buildingId;

      setSelectedEventId(null);

      try {
        await cancelEventMutation({
          eventId,
          buildingId,
          body: { action: 'CANCEL', comments: reason },
          etag,
        }).unwrap();
        setCancelDialogOpen(false);
        closePanel();
        return true;
      } catch (apiError) {
        setSelectedEventId(eventId as string);
        setError(extractApiErrorMessage(apiError) ?? mapActionError(apiError));
        return false;
      }
    },
    [selectedEventId, event?.buildingId, cancelEventMutation, closePanel, etag]
  );

  const confirmCancellation = useCallback(async () => {
    if (!selectedEventId || !event?.buildingId) return false;
    setError(null);

    const eventId = selectedEventId as EventId;
    const buildingId = event.buildingId;

    setSelectedEventId(null);

    try {
      await cancelEventMutation({
        eventId,
        buildingId,
        body: { action: 'CONFIRM_CANCELLATION' },
        etag,
      }).unwrap();
      closePanel();
      return true;
    } catch (apiError) {
      setSelectedEventId(eventId as string);
      setError(extractApiErrorMessage(apiError) ?? mapActionError(apiError));
      return false;
    }
  }, [selectedEventId, event?.buildingId, cancelEventMutation, closePanel, etag]);

  const markNoOccurrence = useCallback(async () => {
    if (!selectedEventId || !event?.buildingId) return false;
    setError(null);

    const eventId = selectedEventId as EventId;
    const buildingId = event.buildingId;

    setSelectedEventId(null);

    try {
      await markNoOccurrenceMutation({
        eventId,
        buildingId,
        etag,
      }).unwrap();
      closePanel();
      return true;
    } catch (apiError) {
      setSelectedEventId(eventId as string);
      setError(extractApiErrorMessage(apiError) ?? mapActionError(apiError));
      return false;
    }
  }, [selectedEventId, event?.buildingId, markNoOccurrenceMutation, closePanel, etag]);

  const saveEdit = useCallback(
    async (input: EventReviewEditInput) => {
      if (!selectedEventId || !event?.buildingId || !event) {
        return false;
      }

      // Re-evaluate at save time, including when the event ended during editing.
      const isPastEvent = Date.now() > Date.parse(event.eventEndDate);
      const originalSpaces: EventSpaceSelection[] = event.spaces?.length
        ? event.spaces
        : event.spaceId
          ? [{ spaceId: event.spaceId, spaceName: event.spaceName ?? '', selectedSetupCodes: [] }]
          : [];
      // Block save if there are empty (unselected) space rows
      if (!isPastEvent && selectedSpaces.some((s) => !s.spaceId)) {
        setError(null);
        setTimeout(() => {
          setError(t('reviewPanel.emptySpaceRowError'));
        }, 0);
        return false;
      }

      // Block save if any space row has no room setup selected
      if (
        !isPastEvent &&
        selectedSpaces.some((s) => s.spaceId && s.selectedSetupCodes.length === 0)
      ) {
        setError(null);
        setTimeout(() => {
          setError(t('reviewPanel.emptySetupError'));
        }, 0);
        return false;
      }

      const parsedAttendees = Number.parseInt(input.attendees, 10);
      const chosenSpaceId = selectedSpaceId ?? event.spaces?.[0]?.spaceId ?? event.spaceId;
      const chosenSpace = spaces.find((item) => item.spaceId === chosenSpaceId);
      const mutationBuildingId = chosenSpace?.buildingId ?? event.buildingId;

      if (!mutationBuildingId) {
        return false;
      }

      const effectiveTimeZone = effectiveBuilding?.gmtZone ?? event.building?.gmtZone;

      const nextStartDate = isPastEvent
        ? event.eventStartDate
        : mergeDateTimeWithBuildingTimeZone(
            input.startDate,
            input.startTime,
            event.eventStartDate,
            effectiveTimeZone
          );
      const nextEndDate = isPastEvent
        ? event.eventEndDate
        : mergeDateTimeWithBuildingTimeZone(
            input.endDate,
            input.endTime,
            event.eventEndDate,
            effectiveTimeZone
          );
      const rawBusinessGroupCode = (input as { businessGroupCode?: unknown }).businessGroupCode;
      const rawBusinessJustification = (input as { businessJustification?: unknown })
        .businessJustification;
      const rawRoomSetupCodes = (input as { roomSetupCodes?: unknown }).roomSetupCodes;
      const rawAdditionalInformation = (input as { additionalInformation?: unknown })
        .additionalInformation;
      const nextBusinessGroupCode =
        typeof rawBusinessGroupCode === 'string' ? rawBusinessGroupCode : undefined;
      const nextBusinessJustification =
        typeof rawBusinessJustification === 'string' ? rawBusinessJustification : undefined;
      const nextAdditionalInformation =
        typeof rawAdditionalInformation === 'string' ? rawAdditionalInformation : undefined;
      const nextRoomSetupCodes =
        Array.isArray(rawRoomSetupCodes) &&
        rawRoomSetupCodes.every((value) => typeof value === 'string')
          ? rawRoomSetupCodes
          : undefined;
      const nextAttendees = Number.isFinite(parsedAttendees)
        ? parsedAttendees
        : event.numberOfAttendees;
      const nextCostCenter = normalizeCostCenter(input.ioCcNumber);
      const nextHasOnSiteContact = input.hasOnSiteContact;
      const filteredContacts = nextHasOnSiteContact
        ? input.onSiteContacts.filter((c) => c.name.trim() || c.email.trim())
        : [];
      const nextOnSiteContacts = nextHasOnSiteContact
        ? filteredContacts.map((c, i) => ({
            name: c.name,
            email: c.email,
            phone: event.onSiteContacts?.[i]?.phone,
          }))
        : [];

      const patchBody: PatchEventRequest = {
        ...(nextStartDate !== event.eventStartDate ? { eventStartDate: nextStartDate } : {}),
        ...(nextEndDate !== event.eventEndDate ? { eventEndDate: nextEndDate } : {}),
        ...(!isPastEvent && nextAttendees !== event.numberOfAttendees
          ? { numberOfAttendees: nextAttendees }
          : {}),
        ...(input.attendeeType !== (event.attendeeTypeCode ?? '')
          ? { attendeeTypeCode: input.attendeeType }
          : {}),
        ...(input.businessPurpose !== (event.visitPurposeCode ?? '')
          ? { visitPurposeCode: input.businessPurpose }
          : {}),
        ...(nextBusinessGroupCode !== undefined &&
        nextBusinessGroupCode !== (event.businessGroupCode ?? '')
          ? { businessGroupCode: nextBusinessGroupCode }
          : {}),
        ...(nextBusinessJustification !== undefined &&
        nextBusinessJustification !== (event.businessJustification ?? '')
          ? { businessJustification: nextBusinessJustification }
          : {}),
        ...(nextAdditionalInformation !== undefined &&
        nextAdditionalInformation !== (event.additionalInformation ?? '')
          ? { additionalInformation: nextAdditionalInformation }
          : {}),
        ...(() => {
          const rawSponsorName = (input as { sponsorName?: unknown }).sponsorName;
          const rawSponsorEmail = (input as { sponsorEmail?: unknown }).sponsorEmail;
          const nextSponsorName = typeof rawSponsorName === 'string' ? rawSponsorName : undefined;
          const nextSponsorEmail =
            typeof rawSponsorEmail === 'string' ? rawSponsorEmail : undefined;
          const costCenterChanged = nextCostCenter !== (event.sponsor?.costCenter ?? '');
          const nameChanged =
            nextSponsorName !== undefined && nextSponsorName !== (event.sponsor?.name ?? '');
          const emailChanged =
            nextSponsorEmail !== undefined && nextSponsorEmail !== (event.sponsor?.email ?? '');
          if (costCenterChanged || nameChanged || emailChanged) {
            return {
              sponsor: {
                ...(costCenterChanged ? { costCenter: nextCostCenter } : {}),
                ...(nameChanged ? { name: nextSponsorName } : {}),
                ...(emailChanged ? { email: nextSponsorEmail } : {}),
              },
            };
          }
          return {};
        })(),
        ...(nextHasOnSiteContact !== (event.hasOnSiteContact ?? false)
          ? { hasOnSiteContact: nextHasOnSiteContact }
          : {}),
        ...(() => {
          const prevContacts = event.onSiteContacts ?? [];
          const changed =
            nextOnSiteContacts.length !== prevContacts.length ||
            nextOnSiteContacts.some((c, i) => !isSameContact(c, prevContacts[i]));
          return changed ? { onSiteContacts: nextOnSiteContacts } : {};
        })(),
        ...(input.hasCatering !== (event.hasCatering ?? false)
          ? { hasCatering: input.hasCatering }
          : {}),
        ...(input.filmingRequired !== (event.filmingRequired ?? false)
          ? { filmingRequired: input.filmingRequired }
          : {}),
        ...(input.overnightStorage !== (event.overnightStorageRequired ?? false)
          ? { overnightStorageRequired: input.overnightStorage }
          : {}),
        ...(input.shipments !== (event.shipmentsExpected ?? false)
          ? { shipmentsExpected: input.shipments }
          : {}),
      };

      const currentSpaceId = event.spaces?.[0]?.spaceId ?? event.spaceId;
      const effectiveSpaceId = chosenSpace?.spaceId ?? currentSpaceId;
      const currentSetupCodes = getPreferredSetupCodes(event);
      const nextSetupCodes =
        nextRoomSetupCodes !== undefined ? nextRoomSetupCodes : currentSetupCodes;
      const setupChanged =
        nextRoomSetupCodes !== undefined &&
        !areSameSetupCodes(nextRoomSetupCodes, currentSetupCodes);
      const spaceChanged = Boolean(chosenSpaceId && chosenSpaceId !== currentSpaceId);
      const buildingChanged = spaceChanged && mutationBuildingId !== event.buildingId;

      if (!isPastEvent && (spaceChanged || setupChanged)) {
        const nextBuildingId = chosenSpace?.buildingId ?? mutationBuildingId;
        const nextBuilding = buildings.find((b) => b.buildingId === nextBuildingId);
        patchBody.spaces = [
          {
            buildingId: nextBuildingId,
            buildingName: nextBuilding?.buildingName ?? event.buildingName ?? '',
            spaceId: effectiveSpaceId as unknown as import('@/store/models/ids').SpaceId,
            spaceName:
              chosenSpace?.spaceName ?? event.spaces?.[0]?.spaceName ?? event.spaceName ?? '',
            selectedSetupCodes: nextSetupCodes,
          },
        ];
      }

      // Send multi-space selection if selectedSpaces has entries
      if (
        !isPastEvent &&
        selectedSpaces.length > 0 &&
        (selectedSpaces.length !== originalSpaces.length ||
          selectedSpaces.some(
            (s, i) =>
              s.spaceId !== originalSpaces[i]?.spaceId ||
              !areSameSetupCodes(s.selectedSetupCodes, originalSpaces[i]?.selectedSetupCodes ?? [])
          ))
      ) {
        const validSpaces = selectedSpaces.filter((s) => s.spaceId);
        if (validSpaces.length > 0) {
          patchBody.spaces = validSpaces.map((s) => {
            const available = spaces.find((item) => item.spaceId === s.spaceId);
            const spaceBuildingId = available?.buildingId ?? mutationBuildingId;
            const spaceBuilding = buildings.find((b) => b.buildingId === spaceBuildingId);
            return {
              buildingId: spaceBuildingId,
              buildingName: spaceBuilding?.buildingName ?? event.buildingName ?? '',
              spaceId: s.spaceId as unknown as import('@/store/models/ids').SpaceId,
              spaceName: s.spaceName,
              selectedSetupCodes: s.selectedSetupCodes,
            };
          });
        }
      }

      if (!isPastEvent && buildingChanged && !patchBody.spaces) {
        // Building changed without a triggering spaceChanged branch above; ensure the spaces entry still carries new building identity.
        const nextBuilding = buildings.find((b) => b.buildingId === mutationBuildingId);
        patchBody.spaces = [
          {
            buildingId: mutationBuildingId,
            buildingName: nextBuilding?.buildingName ?? event.buildingName ?? '',
          },
        ];
      }

      if (Object.keys(patchBody).length === 0) {
        return true;
      }

      setError(null);

      try {
        await patchEventMutation({
          params: {
            eventId: selectedEventId as EventId,
            buildingId: event.buildingId,
            etag,
          },
          body: patchBody,
        }).unwrap();
        refetch();
        return true;
      } catch (apiError) {
        setError(extractApiErrorMessage(apiError) ?? mapActionError(apiError));
        return false;
      }
    },
    [
      selectedEventId,
      event,
      selectedSpaceId,
      selectedSpaces,
      spaces,
      buildings,
      effectiveBuilding?.gmtZone,
      patchEventMutation,
      etag,
      refetch,
      t,
    ]
  );

  const openDeclineDialog = useCallback(() => {
    setDeclineDialogOpen(true);
    setError(null);
  }, []);

  const closeDeclineDialog = useCallback(() => {
    setDeclineDialogOpen(false);
    setError(null);
  }, []);

  const openCancelDialog = useCallback(() => {
    setCancelDialogOpen(true);
    setError(null);
  }, []);

  const closeCancelDialog = useCallback(() => {
    setCancelDialogOpen(false);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const resendSurvey = useCallback(async () => {
    if (!selectedEventId || !event?.buildingId) return false;
    setError(null);

    try {
      await resendSurveyMutation({
        eventId: selectedEventId as EventId,
        buildingId: event.buildingId,
      }).unwrap();
      return true;
    } catch (apiError) {
      setError(extractApiErrorMessage(apiError) ?? mapActionError(apiError));
      return false;
    }
  }, [selectedEventId, event?.buildingId, resendSurveyMutation]);

  return {
    selectedEventId,
    panelOpen,
    selectedSpaceId,
    selectedSpaces,
    isConfirming,
    isDeclining,
    isCancelling,
    isMarkingNoOccurrence,
    isSavingEdit,
    declineDialogOpen,
    cancelDialogOpen,
    error,
    event,
    building: effectiveBuilding,
    spaces,
    buildings,
    selectedSpaceStateProvince,
    isLoading,
    isError,
    openPanel,
    closePanel,
    setSelectedSpace,
    setSelectedSpaces,
    clearSelectedSpace,
    confirmEvent,
    declineEvent,
    cancelEvent,
    confirmCancellation,
    markNoOccurrence,
    saveEdit,
    openDeclineDialog,
    closeDeclineDialog,
    openCancelDialog,
    closeCancelDialog,
    refetch,
    clearError,
    isSearchingSpaces,
    isResendingSurvey,
    resendSurvey,
    fetchSpacesAvailability,
  };
}
